/**
 * screens/MyCash — Custodian
 * ──────────────────────────────────────────────────────────────────────────
 * What you hold, and everything you can do with it.
 *
 * Two columns at `auto-fit minmax(300px, 1fr)` with `align-items: start`, so a
 * short balance card does not stretch to match a long activity list beside it.
 *
 * LEFT   the balance card (3px ink strip, 34px mono figure, its as-of time, the
 *        purpose block with a progress bar where applicable) and a 2×2 grid of
 *        38px action tiles.
 * RIGHT  recent activity — inflows in ink, outflows in grey.
 *
 * A custodian can record FOUR things and nothing else: spend, collection,
 * deposit, replenishment. They cannot allocate to themselves — that would be
 * handing yourself company money and having the ledger agree — and the API
 * enforces it independently of this screen.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useCallback, useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import {
  Button,
  Card,
  CardHeader,
  Chip,
  Eyebrow,
  Grid,
  Loading,
  Money,
  PageContainer,
  PageHeader,
  ProgressBar,
} from '../../components';
import { neutral, status as statusTones } from '../../theme/colors';
import { radii } from '../../theme/radii';
import { gridMin } from '../../theme/spacing';
import { mono, numeric, sans, size as fontSize, leading } from '../../theme/typography';
import { ellipsisStyle } from '../../theme/webStyle';
import { useAsyncData } from '../../lib/useAsyncData';
import { date, dateTime } from '../../lib/dates';
import { dotted, sentenceCase } from '../../lib/format';
import { format, ratio, toBigInt } from '../../lib/money';
import { endRuleLabel, txnDirection, txnLabel } from '../../lib/status';
import { fetchMyAssignments, fetchMyTransactions } from '../../services/cash/cash.service';
import type { Assignment, Transaction } from '../../schemas/cash';
import { useConfig } from '../../context/ConfigContext';
import { useShell } from '../../context/ShellContext';
import CustodianOnboarding from './components/CustodianOnboarding';

/** How many rows the "Recent activity" card shows before "See all". */
const RECENT_LIMIT = 6;

export function MyCashScreen() {
  const { dataVersion, openModal, setView } = useShell();
  const { categories } = useConfig();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = useCallback(
    async (): Promise<{ assignments: Assignment[]; transactions: Transaction[] }> => {
      const [assignments, transactions] = await Promise.all([
        fetchMyAssignments(),
        fetchMyTransactions(),
      ]);
      return { assignments, transactions };
    },
    [],
  );

  const { data, loading } = useAsyncData(load, [], {
    version: dataVersion,
    errorTitle: 'Could not load your cash',
  });

  // Memoised for the same reason as in Approvals: a fresh `[]` each render
  // would invalidate the two memos below on every pass.
  const assignments = useMemo(() => data?.assignments ?? [], [data]);

  // The switcher's selection, falling back to the first assignment. Held as an
  // id rather than an index so a refetch that reorders the list does not switch
  // which assignment the person is looking at.
  const current = useMemo(
    () => assignments.find((a) => a.id === selectedId) ?? assignments[0] ?? null,
    [assignments, selectedId],
  );

  const recent = useMemo(() => {
    if (!data || !current) return [];
    // "My transactions" spans every assignment the person holds, so the card
    // has to filter to the one on screen — otherwise the activity list would
    // not match the balance above it.
    return data.transactions.slice(0, RECENT_LIMIT);
  }, [data, current]);

  const categoryCode = useCallback(
    (id: string | null) => categories.find((category) => category.id === id)?.code ?? null,
    [categories],
  );

  /** The purpose wallet's progress: how much of the original amount is gone. */
  const purpose = useMemo(() => {
    if (!current || current.scope !== 'purpose' || !current.limit_minor) return null;
    const limit = toBigInt(current.limit_minor);
    const balance = toBigInt(current.balance_minor);
    const spent = limit - balance;
    return {
      spentMinor: (spent < 0n ? 0n : spent).toString(),
      leftMinor: current.balance_minor,
      // Spent as a share of the original amount.
      spentRatio: ratio((spent < 0n ? 0n : spent).toString(), current.limit_minor),
    };
  }, [current]);

  if (loading && !data) {
    return (
      <PageContainer>
        <Loading />
      </PageContainer>
    );
  }

  if (!current) {
    return (
      <PageContainer testID="screen-mycash">
        <CustodianOnboarding />
      </PageContainer>
    );
  }

  return (
    <PageContainer testID="screen-mycash">
      <PageHeader
        title="My cash"
        subtitle="What you hold, and everything you can do with it."
        right={
          // The switcher appears only when there is something to switch
          // between. One assignment needs no chooser.
          assignments.length > 1 ? (
            <View style={styles.pickers}>
              {assignments.map((assignment) => (
                <Chip
                  key={assignment.id}
                  label={assignment.purpose_label || sentenceCase(assignment.scope)}
                  shape="pill"
                  selected={assignment.id === current.id}
                  onPress={() => setSelectedId(assignment.id)}
                />
              ))}
            </View>
          ) : undefined
        }
      />

      {/* 16px between the two custodian columns, from the design source. */}
      <Grid min={gridMin.custodian} gap={16} alignStart>
        {/* ── Left column ─────────────────────────────────────────────── */}
        <View style={styles.column}>
          <Card accent={neutral.ink}>
            <View style={styles.balanceBody}>
              <Text style={styles.holdingLabel}>You are holding</Text>
              <Money
                value={current.balance_minor}
                cur={current.cur}
                size={fontSize.balance}
                weight="500"
                style={styles.balance}
              />
              <Text style={styles.asOf}>
                as of {dateTime(current.balance_as_of ?? current.opened_at)}
              </Text>

              {current.scope === 'purpose' && (
                <View style={styles.purposeBox}>
                  <Text style={styles.purposeLabel}>{current.purpose_label ?? 'Purpose'}</Text>
                  <Text style={styles.purposeMeta}>
                    {dotted(
                      current.purpose_expires_at
                        ? `Expires ${date(current.purpose_expires_at)}`
                        : null,
                      purpose
                        ? `spent ${format(purpose.spentMinor, current.cur)}, ${format(
                            purpose.leftMinor,
                            current.cur,
                          )} left`
                        : null,
                    )}
                  </Text>
                  {!!purpose && (
                    <View style={styles.purposeBar}>
                      <ProgressBar ratio={purpose.spentRatio} height={6} />
                    </View>
                  )}
                  <Text style={styles.endRule}>{endRuleLabel(current.end_rule)}</Text>
                </View>
              )}

              {/* The amber prompt: finance is waiting on this custodian. */}
              {current.stale && (
                <View style={styles.prompt}>
                  <Text style={styles.promptText}>
                    Finance is waiting on a count of this cash.
                  </Text>
                </View>
              )}
            </View>
          </Card>

          <Card>
            <View style={styles.actionHeader}>
              <Eyebrow>Record a transaction</Eyebrow>
            </View>
            <View style={styles.actionGrid}>
              <View style={styles.actionCell}>
                <Button
                  label="Log a spend"
                  variant="drawer"
                  size="xl"
                  fullWidth
                  style={styles.tile}
                  onPress={() =>
                    openModal({ kind: 'transaction', assignmentId: current.id, txnType: 'spend' })
                  }
                />
              </View>
              <View style={styles.actionCell}>
                <Button
                  label="Collection"
                  variant="drawer"
                  size="xl"
                  fullWidth
                  style={styles.tile}
                  onPress={() =>
                    openModal({
                      kind: 'transaction',
                      assignmentId: current.id,
                      txnType: 'collection',
                    })
                  }
                />
              </View>
              <View style={styles.actionCell}>
                <Button
                  label="Deposit"
                  variant="drawer"
                  size="xl"
                  fullWidth
                  style={styles.tile}
                  onPress={() =>
                    openModal({ kind: 'transaction', assignmentId: current.id, txnType: 'deposit' })
                  }
                />
              </View>
              <View style={styles.actionCell}>
                <Button
                  label="All transactions"
                  variant="drawer"
                  size="xl"
                  fullWidth
                  style={styles.tile}
                  onPress={() => setView('myledger')}
                />
              </View>
            </View>
            <Text style={styles.actionNote}>
              Receipts are kept as evidence. Corrections are reversals, never edits.
            </Text>
          </Card>
        </View>

        {/* ── Right column ────────────────────────────────────────────── */}
        <Card>
          <CardHeader
            title="Recent activity"
            filled
            right={
              <Button
                label="See all"
                size="sm"
                variant="outline"
                onPress={() => setView('myledger')}
              />
            }
          />
          {recent.map((txn) => {
            const direction = txnDirection(txn.txn_type, txn.is_reversal);
            return (
              <View key={txn.id} style={styles.activityRow}>
                <View style={styles.activityText}>
                  <Text numberOfLines={1} style={styles.activityTitle}>
                    {dotted(txnLabel(txn.txn_type), categoryCode(txn.category_id))}
                  </Text>
                  <Text style={styles.activityStamp}>{dateTime(txn.occurred_at)}</Text>
                </View>
                <Money
                  value={txn.amount_minor}
                  cur={txn.cur}
                  direction={direction}
                  align="right"
                  color={direction === 'in' ? neutral.ink : neutral.grey600}
                />
              </View>
            );
          })}
        </Card>
      </Grid>
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  pickers: { flexDirection: 'row', gap: 6, flexWrap: 'wrap' },
  column: { gap: 16 },

  balanceBody: { paddingTop: 18, paddingHorizontal: 20, paddingBottom: 20 },
  holdingLabel: {
    ...sans('400'),
    fontSize: fontSize.metaSm,
    letterSpacing: 0.88,
    textTransform: 'uppercase',
    color: neutral.grey600,
    marginBottom: 10,
  },
  balance: { letterSpacing: -0.34 },
  asOf: { ...mono('400'), fontSize: fontSize.tableHeader, ...numeric, color: neutral.grey400, marginTop: 7 },

  purposeBox: {
    marginTop: 16,
    padding: 13,
    backgroundColor: neutral.surface2,
    borderWidth: 1,
    borderColor: neutral.border,
    borderRadius: radii.card,
  },
  purposeLabel: { ...sans('500'), fontSize: fontSize.body, color: neutral.ink, marginBottom: 2 },
  purposeMeta: { ...sans('400'), fontSize: fontSize.label, color: neutral.grey600 },
  purposeBar: { marginTop: 11, marginBottom: 9 },
  endRule: { ...sans('400'), fontSize: fontSize.meta, color: neutral.ink2 },

  prompt: {
    marginTop: 16,
    paddingVertical: 12,
    paddingHorizontal: 13,
    borderWidth: 1,
    borderColor: statusTones.pending.border,
    backgroundColor: statusTones.pending.bg,
    borderRadius: radii.card,
  },
  promptText: {
    ...sans('400'),
    fontSize: fontSize.button,
    lineHeight: leading(fontSize.button),
    color: statusTones.pending.text,
  },

  actionHeader: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: neutral.ruleLight,
  },
  actionGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    paddingVertical: 14,
    paddingHorizontal: 16,
  },
  actionCell: { flexBasis: '47%', flexGrow: 1, minWidth: 130 },
  // 38px tiles, one step taller than the drawer's 34px actions.
  tile: { height: 38, paddingHorizontal: 13 },
  actionNote: {
    ...sans('400'),
    fontSize: fontSize.meta,
    color: neutral.grey400,
    paddingHorizontal: 16,
    paddingBottom: 14,
  },

  activityRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 14,
    paddingVertical: 13,
    paddingHorizontal: 16,
    // The custodian list uses the LIGHTEST divider in the palette.
    borderBottomWidth: 1,
    borderBottomColor: neutral.ruleLighter,
  },
  activityText: { flex: 1, minWidth: 0, gap: 1 },
  activityTitle: {
    ...sans('400'),
    fontSize: fontSize.body,
    color: neutral.ink,
    textTransform: 'capitalize',
    ...ellipsisStyle,
  },
  activityStamp: { ...sans('400'), fontSize: fontSize.metaSm, color: neutral.grey400 },
});

export default MyCashScreen;
